import { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';
import EditorManager from 'Editor/services/EditorManager';
import {
  SectionHeader,
  ToggleGroup,
  RadioGroup,
  Tooltip,
  Checkbox,
  Modal,
  Icon,
  Select,
  Button,
} from '@ets/design-system';

import { EditorService, InstanceService, Logger } from '_common/services';
import { toast } from 'utils/toast';

import { closeAndResetModal, initialState, updateModal } from '_common/modals/ModalsSlice';
import { selectTocItemsParents } from 'Editor/redux/TocSlice';
import { setAppLoading } from 'App/redux/appSlice';
import { setLoadingValue } from 'Editor/redux/EditorStatusSlice';

import DocumentContent from './DocumentContent/DocumentContent';
import TemplateDetails from './TemplateDetails/TemplateDetails';
import TemplateOptions from './TemplateOptions/TemplateOptions';
import type { ElementProps } from './DocumentContent/Element/Element';
import type { TemplateOptionsProps } from './TemplateOptions/TemplateOptions';
import useConditionalData from './useConditionalData';

import styles from './ExportDocumentModal.module.scss';

const ExportDocumentModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const objectId = useSelector((state) => state.modals.ExportDocumentModal.objectId);
  const isOpen = useSelector((state) => state.modals.open.ExportDocumentModal);
  const exportType = useSelector(
    (state) =>
      state.modals.ExportDocumentModal.exportType || initialState.ExportDocumentModal.exportType,
  );
  const initialExportFormat = useSelector(
    (state) =>
      state.modals.ExportDocumentModal.initialExportFormat ||
      initialState.ExportDocumentModal.initialExportFormat,
  );
  const exporting = useSelector(
    (state) =>
      state.modals.ExportDocumentModal.exporting || initialState.ExportDocumentModal.exporting,
  );
  const elementsParentsMapper = useSelector(selectTocItemsParents);

  const isEditor = useSelector((state) => state.editor.status.visible);
  const { navigationList, navigationData, template, object } = useConditionalData();

  const [exportFormat, setExportFormat] = useState(initialExportFormat);
  const [exportComments, setExportComments] = useState(true);
  const [exportSuggestions, setExportSuggestions] = useState(true);
  const [exportTasks, setExportTasks] = useState(true);
  const [customizedContent, setCustomizedContent] = useState<'all' | 'customized'>('all');
  const [selectedItems, setSelectedItems] = useState<ElementProps['selectedItems']>({});
  const [templateOptions, setTemplateOptions] = useState<TemplateOptionsProps['options']>({
    show: false,
  });
  const [referenceFormat, setReferenceFormat] = useState<'original' | 'convertTo'>('original');
  const [convertToOption, setConvertToOption] = useState({
    value: 'docx',
    label: 'Word',
  });
  const [modalIsHidden, setModalIsHidden] = useState(false);
  const timeoutRef = useRef<number>(undefined);

  useEffect(() => {
    if (!isEditor) {
      return;
    }

    const manager = EditorManager.getInstance();
    if (manager) {
      if (manager.structureHasAutomaticUpdates()) {
        setTemplateOptions({
          show: true,
          update: true,
          template: manager.getWordTemplate(),
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const newSelectedItems = { ...selectedItems };
    const elementIds = Object.keys(selectedItems);
    if (elementIds.length > 0) {
      const flatElements: typeof newSelectedItems = {};
      const elementsToSearch = [...navigationList];
      while (elementsToSearch.length > 0) {
        const elementId = elementsToSearch.shift();
        if (elementId) {
          const element = navigationData[elementId];
          flatElements[element.id] = true;
          if (element.childNodes.length > 0) {
            elementsToSearch.unshift(...element.childNodes);
          }
        }
      }
      elementIds.forEach((id) => {
        newSelectedItems[id] = flatElements[id];
      });
      setSelectedItems(newSelectedItems);
    } else {
      setCustomizedContent('all');
    }
  }, [navigationList, navigationData]);

  useEffect(() => {
    if (exporting) {
      checkStatus();
    }
    return () => {
      clearTimeout(timeoutRef?.current);
    };
  }, [exporting]);

  const handleSelectedItem = (id: Editor.ToCItem['id']) => {
    const selected = selectedItems[id] === 'indeterminate' ? true : !selectedItems[id];
    const selectedItem = navigationData[id];
    const newSelections = { ...selectedItems, [id]: selected };
    // Check if the parent has to be set to indeterminate
    // Might not have a parent
    let parentId = elementsParentsMapper[id];
    while (parentId) {
      let status: (typeof newSelections)[keyof typeof newSelections] | null = null;
      const parent = navigationData[parentId];
      if (!newSelections[parent.id]) {
        break;
      }
      for (let i = 0; i < parent.childNodes.length; i++) {
        const childId = parent.childNodes[i];
        const child = navigationData[childId];
        if (Object.prototype.hasOwnProperty.call(newSelections, child.id)) {
          newSelections[child.id] = false;
        }
        if (status === null && newSelections[child.id]) {
          status = newSelections[child.id];
        } else if (status !== newSelections[child.id]) {
          status = 'indeterminate';
          break;
        }
      }
      if (status !== null) {
        newSelections[parent.id] = status;
      }
      parentId = elementsParentsMapper[parent.id];
    }

    // Set the same state for every children of every level
    const items = [...selectedItem.childNodes];
    while (items.length > 0) {
      const itemId = items.shift();
      if (itemId) {
        const item = navigationData[itemId];
        items.push(...item.childNodes);
        newSelections[item.id] = selected;
      }
    }

    setSelectedItems(newSelections);
  };

  const toggleExportFormat = (value: typeof exportFormat) => {
    setExportFormat(value);
  };
  const toggleExportComments = () => {
    setExportComments((value) => !value);
  };
  const toggleExportSuggestions = () => {
    setExportSuggestions((value) => !value);
  };

  const toggleExportTasks = () => {
    setExportTasks((value) => !value);
  };

  const handleDocumentContent = (id: 'all' | 'customized') => {
    setCustomizedContent(id);
  };

  const download = () => {
    new EditorService()
      .download(objectId)
      .then((response) => {
        const name = decodeURIComponent(response.headers['x-download-filename']);
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;

        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((error) => Logger.captureException(error));
  };

  const handleLoadingState = (value: boolean) => {
    if (!isEditor) {
      dispatch(setAppLoading({ isOpen: value }));
    } else {
      dispatch(setLoadingValue(value));
    }
  };

  const checkStatus = () => {
    timeoutRef.current = window.setTimeout(() => {
      new InstanceService()
        .checkJobStatus({ jobs: [exporting] })
        .then(({ data }) => {
          //@ts-expect-error APISpec: endpoint response type its not defined
          switch (data[exporting].status) {
            case 'queued':
            case 'started':
              checkStatus();
              return;
            case 'finished':
              handleLoadingState(false);

              if (exportType === 'simple') {
                download();
                toast({
                  variant: 'success',
                  title: 'DODOC_DOCUMENT_EXPORTED',
                  message: 'DODOC_DOCUMENT_EXPORTED_MESSAGE',
                  messageValues: {
                    name: object?.name ?? '',
                  },
                  options: { duration: Infinity },
                  cta: {
                    children: <FormattedMessage id="DOWNLOAD_MANUALLY" />,
                    onClick: download,
                  },
                });
              }
              close();
              return;
            case 'failed':
              handleLoadingState(false);
              setModalIsHidden(false);
              return;
            default:
              handleLoadingState(false);
              close();
          }
        })
        .catch(() => {
          handleLoadingState(false);
          close();
        });
    }, 2500);
  };

  const exportDocument = () => {
    const params: {
      template?: typeof template;
      format: typeof exportFormat;
      keep_comments?: typeof exportComments;
      keep_suggestions?: typeof exportSuggestions;
      nodes?: string[];
      keep_tasks?: typeof exportTasks;
      update_styles?: typeof templateOptions.update;
      citations?: typeof referenceFormat | string;
    } = {
      template: template || '_blank',
      format: exportFormat,
      keep_comments: exportComments,
      keep_suggestions: exportSuggestions,
      nodes: Object.keys(selectedItems)
        .filter((id) => selectedItems[id])
        .map((id) => navigationData[id].target.split(':')[0]),
    };
    if (exportFormat === 'docx') {
      params.keep_tasks = exportTasks;
      params.update_styles = templateOptions.update;
      params.citations = referenceFormat === 'convertTo' ? convertToOption.value : referenceFormat;
    }
    if (exportType === 'simple') {
      if (!isEditor) {
        dispatch(
          setAppLoading({
            isOpen: true,
            message: 'EXPORTING_DOCUMENT',
          }),
        );
      } else {
        dispatch(setLoadingValue('EXPORTING_DOCUMENT'));
      }
      setModalIsHidden(true);
      new EditorService()
        .exportDocument('document', objectId, params)
        .then(({ data }) => {
          dispatch(
            updateModal({
              modal: 'ExportDocumentModal',
              data: { exporting: data.id },
            }),
          );
        })
        .catch(() => {
          handleLoadingState(false);
          dispatch(closeAndResetModal('ExportDocumentModal'));
          toast({
            variant: 'error',
            title: 'global.error',
            message: 'auth.errors.error',
          });
        });
    }
  };

  const close = () => {
    setExportFormat('docx');
    setExportComments(true);
    setExportSuggestions(true);
    setExportTasks(false);
    setTemplateOptions({ show: false });
    setSelectedItems({});
    dispatch(closeAndResetModal('ExportDocumentModal'));
  };

  const modalType = 'export-document';

  return (
    <Modal width="75rem" open={!!isOpen && !modalIsHidden} close={close} testId={modalType}>
      <Modal.Header>
        <FormattedMessage id="EXPORT_DOCUMENT_MODAL_HEADER" values={{ name: object?.name }} />
      </Modal.Header>
      <Modal.Body>
        <>
          <ToggleGroup
            toggles={[
              { value: 'docx', label: 'MS Word' },
              { value: 'html', label: 'HTML' },
            ]}
            selected={exportFormat}
            onToggle={toggleExportFormat}
            size="m"
            testId={modalType}
            fullWidth
          />
          <SectionHeader margin="3rem 0 2rem 0" size="m" testId="template">
            <FormattedMessage id="DOCUMENT_TEMPLATE" />
          </SectionHeader>
          <TemplateDetails objectId={objectId} />
          {exportFormat === 'docx' && templateOptions.show && (
            <TemplateOptions options={templateOptions} setOptions={setTemplateOptions} />
          )}
          <>
            <SectionHeader style={{ margin: '3rem 0 2rem 0' }} size="m" testId="document-content">
              <FormattedMessage id="DOCUMENT_CONTENT" />
            </SectionHeader>
            <RadioGroup
              items={[
                { value: 'all', label: intl.formatMessage({ id: 'ALL' }) },
                {
                  value: 'customized',
                  label: intl.formatMessage({ id: 'CUSTOMIZED' }),
                  disabled: navigationList.length === 0,
                },
              ]}
              value={customizedContent}
              onChange={(e) => handleDocumentContent(e.target.value)}
              direction="vertical"
              size="xs"
              testId={`${modalType}-document-content`}
            />
          </>
          {customizedContent === 'customized' && (
            <DocumentContent selectedItems={selectedItems} setSelectedItems={handleSelectedItem} />
          )}
          <SectionHeader style={{ margin: '3rem 0 2rem 0' }} size="m" testId="reference-format">
            <FormattedMessage id="REFERENCE_FORMAT" />
          </SectionHeader>
          {exportFormat === 'docx' && (
            <div>
              <RadioGroup
                items={[
                  {
                    value: 'original',
                    label: (
                      <div className={styles.referenceKeepOriginal}>
                        {intl.formatMessage({ id: 'KEEP_ORIGINAL_FORMAT' })}
                        <div style={{ marginLeft: '1rem' }}>
                          <Tooltip
                            placement="bottom"
                            content={intl.formatMessage({
                              id: 'KEEP_ORIGINAL_FORMAT_TOOLTIP',
                            })}
                            testId={`${modalType}-keep-original-format-information`}
                          >
                            <span>
                              <Icon icon="circle-info" size="2" testId="info" color="primary" />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    ),
                  },
                  {
                    value: 'convertTo',
                    label: (
                      <div className={styles.convert_container}>
                        <div style={{ margin: '0 3rem 0 0' }}>
                          {intl.formatMessage({ id: 'CONVERT_TO' })}
                        </div>
                        <div onClick={(e) => e.preventDefault()}>
                          <Select
                            variant="standard"
                            disabled={referenceFormat !== 'convertTo'}
                            options={[
                              { value: 'docx', label: 'Word' },
                              { value: 'mendeley', label: 'Mendeley' },
                              { value: 'endnote', label: 'EndNote' },
                            ]}
                            onChange={(newValue) => {
                              if (newValue) {
                                setConvertToOption(newValue);
                              }
                            }}
                            value={convertToOption}
                            width="28rem"
                            menuPosition="fixed"
                            size="m"
                            isClearable={false}
                            testId={`${modalType}-convert-to`}
                          />
                        </div>
                      </div>
                    ),
                  },
                ]}
                value={referenceFormat}
                onChange={(e) => setReferenceFormat(e.target.value)}
                direction="vertical"
                size="xs"
                testId={`${modalType}-reference-format`}
                className={styles.container}
              />
            </div>
          )}
          <>
            <Checkbox
              size="xs"
              checked={exportComments}
              onChange={toggleExportComments}
              style={{ margin: '0 0 2rem 0' }}
              testId={`${modalType}-include-comments`}
            >
              <FormattedMessage id="EXPORT_ALL_COMMENTS" />
            </Checkbox>
            <Checkbox
              size="xs"
              checked={exportSuggestions}
              onChange={toggleExportSuggestions}
              style={{ margin: '0 0 2rem 0' }}
              testId={`${modalType}-include-track-changes`}
            >
              <FormattedMessage id="EXPORT_ALL_SUGGESTIONS" />
            </Checkbox>
            <Tooltip
              content={intl.formatMessage({ id: 'TASKS_CANNOT_BE_EXPORTED_TO_HTML' })}
              visible={exportFormat !== 'docx'}
              placement="bottom"
              testId={`${modalType}-include-tasks-as-comments`}
            >
              <span>
                <Checkbox
                  size="xs"
                  checked={exportFormat === 'html' || exportTasks}
                  onChange={toggleExportTasks}
                  disabled={exportFormat === 'html'}
                  style={{ margin: '0 0 1rem 0' }}
                  testId={`${modalType}-include-tasks-as-comments`}
                >
                  <FormattedMessage id="INCLUDE_TASKS_AS_COMMENTS" />
                </Checkbox>
              </span>
            </Tooltip>
          </>
        </>
      </Modal.Body>
      {!exporting && (
        <Modal.Footer>
          <Button variant="standard" size="m" onClick={close} testId={`${modalType}-cancel`}>
            <FormattedMessage id="global.close" />
          </Button>
          <Button
            size="m"
            variant="primary"
            onClick={exportDocument}
            testId={`${modalType}-submit`}
            style={{ margin: '0 0 0 1rem' }}
          >
            <FormattedMessage id="global.export" />
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ExportDocumentModal;
